// src/pages/HomePage.tsx
// Summit League — Halo Infinite Homepage
// Root domain entry point at summitleague.gg

import React, { useEffect, useState } from 'react';
import axiosClient from '../api/axiosClient';
import LeaderboardPreview from '../components/dashboard/LeaderboardPreview';
import RecentMatches from '../components/dashboard/RecentMatches';
import summitLeagueLogo from '../assets/summit-league-logo.png';
import type {
  DashboardApiResponse,
  DashboardData,
} from '../types/dashboard.types';
import {
  formatDate as formatSeasonDate,
  getStatusLabel,
} from '../components/dashboard/DashboardHelpers';
import type { SeasonStatus } from '../types/dashboard.types';
import '../styles/dashboard.css';
import '../styles/home.css';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const GAME_ID = 1; // Halo Infinite
const SUBDOMAIN_BASE = process.env.REACT_APP_SUBDOMAIN_BASE || '.summitleague.gg';
const PROTOCOL = window.location.protocol;
const ARENA_URL = `${PROTOCOL}//hi${SUBDOMAIN_BASE}`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NewsPost {
  post_id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  categories: string[];
  published_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function getInfoCardStatusClass(status: SeasonStatus): string {
  switch (status) {
    case 'in_progress': return 'sl-h2-info-card-status--active';
    case 'completed':
    case 'archived': return 'sl-h2-info-card-status--complete';
    default: return '';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatChip({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="sl-h2-stat">
      <span className="sl-h2-stat-value">{value}</span>
      <span className="sl-h2-stat-label">{label}</span>
    </div>
  );
}

function NewsCarousel({ posts }: { posts: NewsPost[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (posts.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % posts.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [posts.length, paused]);

  const goTo = (i: number) => setIndex((i + posts.length) % posts.length);

  return (
    <div
      className="sl-h2-news-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="sl-h2-news-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {posts.map((post) => (
          <div className="sl-h2-news-slide" key={post.post_id}>
            <NewsCard post={post} />
          </div>
        ))}
      </div>

      {posts.length > 1 && (
        <>
          <button
            type="button"
            className="sl-h2-news-arrow sl-h2-news-arrow--prev"
            aria-label="Previous article"
            onClick={() => goTo(index - 1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="sl-h2-news-arrow sl-h2-news-arrow--next"
            aria-label="Next article"
            onClick={() => goTo(index + 1)}
          >
            ›
          </button>

          <div className="sl-h2-news-dots">
            {posts.map((post, i) => (
              <button
                key={post.post_id}
                type="button"
                className={`sl-h2-news-dot ${i === index ? 'sl-h2-news-dot--active' : ''}`}
                aria-label={`Go to article ${i + 1}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NewsCard({ post }: { post: NewsPost }) {
  const category = post.categories?.[0] ?? null;
  return (
    <a className="sl-h2-news-card" href={`/news/${post.slug}`}>
      <div className="sl-h2-news-img">
        {post.featured_image_url ? (
          <img
            src={post.featured_image_url}
            alt={post.title}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="sl-h2-news-img-placeholder" />
        )}
      </div>
      <div className="sl-h2-news-body">
        <span
          className="sl-h2-news-category"
          style={category ? undefined : { visibility: 'hidden' }}
        >
          {category || '—'}
        </span>
        <h3 className="sl-h2-news-title">{post.title}</h3>
        <p
          className="sl-h2-news-excerpt"
          style={post.excerpt ? undefined : { visibility: 'hidden' }}
        >
          {post.excerpt || '—'}
        </p>
        <span className="sl-h2-news-date">{formatDate(post.published_at)}</span>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const HomePage: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [news, setNews] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [dashRes, newsRes] = await Promise.allSettled([
          axiosClient.get<DashboardApiResponse>(`/dashboard?game_id=${GAME_ID}`),
          axiosClient.get(`/news?game_id=${GAME_ID}&limit=5`),
        ]);

        if (dashRes.status === 'fulfilled') {
          setDashboard(dashRes.value.data.data);
        }

        if (newsRes.status === 'fulfilled') {
          setNews(newsRes.value.data.data?.posts ?? []);
        }
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const season = dashboard?.season;

  return (
    <div className="sl-h2">

      {/* Hero */}
      <section className="sl-h2-hero">
        <div className="sl-h2-hero-emblem" aria-hidden="true" />

        {/* Centered top band: logo + title + subtitle + CTA */}
        <div className="sl-h2-hero-top">
          <img src={summitLeagueLogo} alt="Summit League" className="sl-h2-hero-logo" />
          <h1 className="sl-h2-hero-title">Halo Infinite</h1>
          <p className="sl-h2-hero-sub">
            Competitive 4v4 arena play. Ranked seasons, Bo3, Bo5 or Bo7 series,<br />
            and full leaderboard tracking.
          </p>

          <a href={ARENA_URL} className="sl-h2-cta">
            Enter Arena
            <span className="sl-h2-cta-arrow">→</span>
          </a>
        </div>

        {/* Split row: stats card (left) / news slider (right), each on equal-height grid rows */}
        <div className="sl-h2-hero-columns">
          <div className="sl-h2-info-card">
            <div className="sl-h2-info-card-header">
              <span className="sl-h2-info-card-season">
                {season?.seasonName ?? (loading ? 'Loading…' : 'No Active Season')}
              </span>
              {season && (
                <span className={`sl-h2-info-card-status ${getInfoCardStatusClass(season.status)}`}>
                  <span className="sl-h2-info-card-status-dot" />
                  {getStatusLabel(season.status)}
                </span>
              )}
            </div>

            <div className="sl-h2-stats">
              {loading ? (
                <div className="sl-h2-stats-loading" />
              ) : (
                <>
                  <StatChip
                    label="Teams"
                    value={dashboard?.leaderboard.length ?? '—'}
                  />
                  <StatChip
                    label="Season"
                    value={season ? `S${season.seasonNumber}` : '—'}
                  />
                  <StatChip
                    label="Days Left"
                    value={season?.status === 'in_progress' ? season.daysLeft : '—'}
                  />
                  {season && (
                    <div className="sl-h2-stat sl-h2-stat--dates">
                      <span className="sl-h2-stat-value sl-h2-stat-value--dates">
                        {formatSeasonDate(season.startDate)} – {formatSeasonDate(season.endDate)}
                      </span>
                      <span className="sl-h2-stat-label">Season Dates</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {news.length > 0 && (
            <div className="sl-h2-hero-slider">
              <NewsCarousel posts={news} />
            </div>
          )}

          {news.length > 0 && (
            <div className="sl-h2-hero-link-row">
              <a href="/news" className="sl-h2-hero-slider-link">All News →</a>
            </div>
          )}
        </div>
      </section>

      {/* Standings */}
      {(dashboard?.leaderboard.length ?? 0) > 0 && (
        <section className="sl-h2-section">
          <LeaderboardPreview
            entries={dashboard!.leaderboard}
            loading={loading}
            onViewFull={() => { window.location.href = `${ARENA_URL}/leaderboard`; }}
            onTeamClick={(teamId) => { window.location.href = `${ARENA_URL}/teams/${teamId}`; }}
          />
        </section>
      )}

      {/* Recent Matches */}
      {(dashboard?.recentMatches.length ?? 0) > 0 && (
        <section className="sl-h2-section">
          <RecentMatches
            matches={dashboard!.recentMatches}
            loading={loading}
            onMatchClick={(matchId) => { window.location.href = `${ARENA_URL}/matches/${matchId}`; }}
          />
        </section>
      )}

      {/* Footer */}
      <footer className="sl-h2-footer">
        &copy; {new Date().getFullYear()} Summit League &middot;{' '}
        <a href={`${ARENA_URL}/auth`}>Sign In</a> &middot;{' '}
        <a href="/legal">Legal</a>
      </footer>

    </div>
  );
};

export default HomePage;
